import { z } from 'zod';
/**
 * Código reservado para el DECLARADO NO RESIDENTE SIN ESTABLECIMIENTO
 * PERMANENTE. El diseño del 347 lo dice literalmente: «En el caso de no
 * residentes sin establecimiento permanente se consignará 99», y entonces las
 * posiciones 79-80 llevan el código de país.
 *
 * ☠️ SOLO VALE EN EL 347. El 190 no lo admite: «En ningún caso se incluirán en
 * el modelo 190 percepciones correspondientes a personas o entidades no
 * residentes en territorio español que no sean contribuyentes del IRPF».
 */
export const AEAT_PROVINCE_NON_RESIDENT = '99';
/**
 * Isla de La Palma. ☠️ SOLO EXISTE EN EL 190, que separa «ISLA DE LA PALMA 53»
 * de «S.C.TENERIFE (a excepción de la Isla de la Palma) 38». El 347 no lo tiene:
 * ahí toda la provincia es 38.
 */
export const AEAT_PROVINCE_LA_PALMA = '53';
/**
 * Residencia habitual en el extranjero. ☠️ SOLO EN EL 190: «Tratándose de
 * percepciones correspondientes a contribuyentes del IRPF con residencia
 * habitual en el extranjero, a los que se refieren los artículos 8.2 y 10 de la
 * Ley del Impuesto […] se consignarán como código de provincia los dígitos 98».
 */
export const AEAT_PROVINCE_ABROAD = '98';
/**
 * Las 52 provincias y ciudades autónomas, más el 99 de no residente.
 *
 * Ordenadas por código y no alfabéticamente: así el desplegable se lee igual
 * que la tabla del diseño de registro cuando alguien la está contrastando.
 */
export const AEAT_PROVINCES = [
    { code: '01', name: 'Araba/Álava' },
    { code: '02', name: 'Albacete' },
    { code: '03', name: 'Alicante/Alacant' },
    { code: '04', name: 'Almería' },
    { code: '05', name: 'Ávila' },
    { code: '06', name: 'Badajoz' },
    { code: '07', name: 'Illes Balears' },
    { code: '08', name: 'Barcelona' },
    { code: '09', name: 'Burgos' },
    { code: '10', name: 'Cáceres' },
    { code: '11', name: 'Cádiz' },
    { code: '12', name: 'Castellón/Castelló' },
    { code: '13', name: 'Ciudad Real' },
    { code: '14', name: 'Córdoba' },
    { code: '15', name: 'A Coruña' },
    { code: '16', name: 'Cuenca' },
    { code: '17', name: 'Girona' },
    { code: '18', name: 'Granada' },
    { code: '19', name: 'Guadalajara' },
    { code: '20', name: 'Gipuzkoa' },
    { code: '21', name: 'Huelva' },
    { code: '22', name: 'Huesca' },
    { code: '23', name: 'Jaén' },
    { code: '24', name: 'León' },
    { code: '25', name: 'Lleida' },
    { code: '26', name: 'La Rioja' },
    { code: '27', name: 'Lugo' },
    { code: '28', name: 'Madrid' },
    { code: '29', name: 'Málaga' },
    { code: '30', name: 'Murcia' },
    { code: '31', name: 'Navarra' },
    { code: '32', name: 'Ourense' },
    { code: '33', name: 'Asturias' },
    { code: '34', name: 'Palencia' },
    { code: '35', name: 'Las Palmas' },
    { code: '36', name: 'Pontevedra' },
    { code: '37', name: 'Salamanca' },
    { code: '38', name: 'Santa Cruz de Tenerife' },
    { code: '39', name: 'Cantabria' },
    { code: '40', name: 'Segovia' },
    { code: '41', name: 'Sevilla' },
    { code: '42', name: 'Soria' },
    { code: '43', name: 'Tarragona' },
    { code: '44', name: 'Teruel' },
    { code: '45', name: 'Toledo' },
    { code: '46', name: 'Valencia/València' },
    { code: '47', name: 'Valladolid' },
    { code: '48', name: 'Bizkaia' },
    { code: '49', name: 'Zamora' },
    { code: '50', name: 'Zaragoza' },
    { code: '51', name: 'Ceuta' },
    { code: '52', name: 'Melilla' },
    { code: AEAT_PROVINCE_LA_PALMA, name: 'Isla de La Palma (solo modelo 190)' },
    { code: AEAT_PROVINCE_ABROAD, name: 'Residencia habitual en el extranjero (solo modelo 190)' },
    { code: AEAT_PROVINCE_NON_RESIDENT, name: 'No residente sin establecimiento permanente (solo modelo 347)' },
];
const PROVINCE_BY_CODE = new Map(AEAT_PROVINCES.map((p) => [p.code, p]));
/** Rótulo de un código de provincia, o el propio código si no existe. */
export function provinceName(code) {
    if (!code)
        return '';
    return PROVINCE_BY_CODE.get(code)?.name ?? code;
}
export function isValidProvinceCode(code) {
    return PROVINCE_BY_CODE.has(code);
}
/**
 * Rango de códigos postales de la isla de La Palma. El 190 le da código propio
 * (53) y excluye expresamente de él la provincia 38, así que de un CP de aquí no
 * se puede deducir ninguno de los dos sin saber el municipio.
 */
const CP_LA_PALMA = /^387[0-8][0-9]$/;
/**
 * Provincia SUGERIDA a partir del código postal.
 *
 * ☠️ ES UNA SUGERENCIA, NO UNA REGLA, Y POR ESO LA ESCRIBE EL FORMULARIO Y NO EL
 * SERVIDOR: lo que una persona vea en pantalla es lo que se guarda, y lo puede
 * cambiar. Tres razones por las que no basta con los dos primeros dígitos:
 *   - Hay enclaves. Petilla de Aragón es de NAVARRA (31) y su código postal es
 *     50686, de Zaragoza, porque Correos lo sirve desde allí.
 *   - La isla de La Palma comparte los CP 387xx con Tenerife y en el 190 tiene
 *     código propio (53). Aquí se devuelve null para que lo elija una persona.
 *   - Los dos diseños piden la provincia del DOMICILIO FISCAL del declarado, y
 *     el código postal de una factura puede ser el de un almacén o una sucursal.
 *
 * Devuelve null si el código postal no es exactamente cinco dígitos. ☠️ No vale
 * quitarle los caracteres que no son dígitos y quedarse con cinco: el eircode
 * irlandés «D02 X285» daría 02285, y de ahí «02, Albacete», que es un dato
 * inventado viajando a Hacienda.
 */
export function provinceCodeFromPostalCode(postalCode) {
    if (!postalCode)
        return null;
    const cp = postalCode.trim();
    if (!/^[0-9]{5}$/.test(cp))
        return null;
    if (CP_LA_PALMA.test(cp))
        return null;
    const code = cp.slice(0, 2);
    // Los códigos que no son una provincia de verdad (99 no residente, 98
    // extranjero, 53 La Palma) no pueden salir nunca de un código postal.
    if (code === AEAT_PROVINCE_NON_RESIDENT)
        return null;
    if (code === AEAT_PROVINCE_ABROAD)
        return null;
    if (code === AEAT_PROVINCE_LA_PALMA)
        return null;
    return PROVINCE_BY_CODE.has(code) ? code : null;
}
/**
 * Prefijos de NIF-IVA de los Estados miembros. Grecia usa EL y no GR, y XI es
 * Irlanda del Norte (protocolo del Brexit): los dos son errores clásicos al
 * escribir esta lista de memoria.
 *
 * ES está incluido a propósito: un NIF-IVA español es intracomunitario igual,
 * aunque el proveedor esté establecido aquí.
 */
export const EU_VAT_PREFIXES = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'HR',
    'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI',
    'SK', 'XI',
];
/**
 * Formato de identificación fiscal española (sin dígito de control):
 *   DNI  8 dígitos + letra · NIE [XYZ] + 7 dígitos + letra
 *   CIF  letra + 7 dígitos + dígito o letra
 */
const ES_FISCAL_ID = /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[A-HJNPQRSUVW][0-9]{7}[0-9A-J])$/;
/**
 * Normaliza una identificación fiscal para COMPARAR, no para imprimir:
 * mayúsculas, sin tildes, sin espacios ni guiones y **sin el prefijo ES**
 * cuando lo que queda detrás es un NIF español válido.
 *
 * ☠️ EL PREFIJO ES ES LA CAUSA DE UN PROVEEDOR DUPLICADO REAL. En producción
 * conviven «A28308484» y «ESA28308484»: es la misma empresa (Sigla S.A.U., los
 * VIPS) con dos subcuentas 400 y dos reglas de tipología, porque la clave de
 * tercero histórica no quitaba el prefijo. En el 347 eso parte su total anual en
 * dos y puede dejar a los dos trozos por debajo del umbral de 3.005,06 €.
 */
export function normalizeFiscalId(raw) {
    if (!raw)
        return '';
    const clean = raw
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');
    if (clean.startsWith('ES') && ES_FISCAL_ID.test(clean.slice(2)))
        return clean.slice(2);
    return clean;
}
/** true si el identificador tiene forma de NIF/NIE/CIF español. */
export function isSpanishFiscalId(raw) {
    return ES_FISCAL_ID.test(normalizeFiscalId(raw));
}
/**
 * ☠️ EL PREFIJO DEL NIF-IVA NO ES EL CÓDIGO DE PAÍS, y en dos casos no coincide.
 *
 * Grecia usa EL en el NIF-IVA, pero su código de país es GR. Irlanda del Norte
 * usa XI, que no es un país: en el Anexo II de la Orden EHA/3496/2011 —que es la
 * tabla que pide el 347 en sus posiciones 79-80— le corresponde GB. Meter el
 * prefijo en el campo de país escribiría «EL» en un fichero que no lo admite.
 */
const PAIS_POR_PREFIJO_IVA = { EL: 'GR', XI: 'GB' };
/** Código de país que corresponde a un prefijo de NIF-IVA comunitario. */
export function countryFromVatPrefix(prefix) {
    return PAIS_POR_PREFIJO_IVA[prefix] ?? prefix;
}
/**
 * Prefijo comunitario de un NIF-IVA extranjero: las dos primeras letras cuando
 * son de un Estado miembro y detrás hay algo con forma de NIF-IVA.
 *
 * «IE4276970QH» → IE (Anthropic Ireland). «ESA28308484» → null, porque detrás
 * del ES hay un CIF español y el proveedor está establecido aquí.
 *
 * ☠️ EL RESTO TIENE QUE LLEVAR ALGÚN DÍGITO, y no es un capricho: de esta
 * deducción salen el país, el NIF-IVA y la marca de no establecido, que es la
 * que decide si una factura se autorrepercute. Sin esa comprobación, un
 * proveedor con «SIN NIF» escrito a mano se leería como SI (Eslovenia) y
 * «DESCONOCIDO» como DE (Alemania). Todo NIF-IVA comunitario lleva dígitos.
 */
export function euCountryFromVatNumber(raw) {
    const clean = normalizeFiscalId(raw);
    if (clean.length < 4)
        return null;
    const prefix = clean.slice(0, 2);
    if (!EU_VAT_PREFIXES.includes(prefix))
        return null;
    if (prefix === 'ES')
        return null;
    const resto = clean.slice(2);
    if (!/^[A-Z0-9]{2,13}$/.test(resto))
        return null;
    if (!/[0-9]/.test(resto))
        return null;
    return prefix;
}
// =====================================================================
// Modelo 190 — claves y subclaves de percepción
// =====================================================================
/**
 * Claves de percepción del 190 que puede llevar LA FACTURA DE UN PROVEEDOR.
 *
 * Son las tres de «rendimientos de actividades económicas». Las demás claves del
 * modelo existen, pero no caben en una ficha de proveedor y por eso no se
 * ofrecen: A, B, C, D, E y F son rendimientos DEL TRABAJO (nóminas, pensiones,
 * desempleo, consejeros, cursos y obras literarias), J y K son imputaciones y
 * premios, y L son las rentas exentas y las dietas exceptuadas de gravamen.
 *
 * ☠️ LAS DIETAS DE LAS HOJAS DE GASTOS VAN AL 190 POR LA CLAVE L, AUNQUE NO
 * LLEVEN RETENCIÓN. No se declaran desde aquí porque quien las cobra es una
 * persona de la casa y no un tercero que factura —el kilometraje y las dietas ni
 * siquiera tienen ficha—: eso es trabajo del generador del 190, en su lado de
 * perceptores, y está anotado para cuando se escriba.
 *
 * El alquiler de un local urbano tampoco está, y es correcto: va al 115
 * trimestral y al 180 anual, nunca al 190. Si lo alquilado es un NEGOCIO y no un
 * local, en cambio, sí va al 190, por la clave I subclave 03.
 */
export const MODEL_190_KEYS = [
    { key: 'G', label: 'Actividades profesionales' },
    { key: 'H', label: 'Actividades agrícolas, ganaderas, forestales y en estimación objetiva' },
    { key: 'I', label: 'Propiedad intelectual o industrial, asistencia técnica y arrendamiento de negocios' },
];
/**
 * Subclaves de la clave G, literales del diseño de registro.
 *
 * ☠️ LA SUBCLAVE ES OBLIGATORIA. El diseño lo dice en las posiciones 79-80:
 * «Tratándose de percepciones correspondientes a las claves B, C, E, F, G, H, I,
 * K y L, deberá consignarse, además, la subclave numérica de dos dígitos». La
 * única cuya exigencia se aplazó al ejercicio 2025 (presentado en enero de 2026)
 * es la clave C, por la disposición final única de la Orden HFP/1286/2023; las
 * de G, H e I rigen desde el ejercicio 2023. Un fichero con la subclave en
 * blanco no lo acepta el formulario.
 */
export const MODEL_190_G_SUBKEYS = [
    { key: '01', label: 'Tipo general del art. 95.1 RIRPF (15 %)' },
    { key: '02', label: 'Recaudadores municipales, mediadores de seguros y delegados de Loterías (7 %)' },
    { key: '03', label: 'Inicio de actividad profesional: tipo reducido del art. 95.1 RIRPF (7 %)' },
    { key: '04', label: 'Tipo específico de la letra d) del art. 95.1 RIRPF' },
    { key: '05', label: 'Propiedad intelectual, tipo general del art. 101.9 LIRPF' },
    { key: '06', label: 'Propiedad intelectual, tipo reducido del art. 101.9 LIRPF' },
    { key: '07', label: 'Anticipos por cesión de la explotación de derechos de autor' },
    { key: '08', label: 'Cesión del derecho a la explotación de la imagen' },
];
/** Subclaves de la clave H (actividades agrícolas, ganaderas y forestales). */
export const MODEL_190_H_SUBKEYS = [
    { key: '01', label: 'Tipo general del art. 95.4.2.º RIRPF' },
    { key: '02', label: 'Engorde de porcino y avicultura: art. 95.4.1.º RIRPF' },
    { key: '03', label: 'Actividades forestales del art. 95.5 RIRPF' },
    { key: '04', label: 'Actividades en estimación objetiva del art. 95.6.2.º RIRPF' },
];
/** Subclaves de la clave I (propiedad intelectual e industrial y asimilados). */
export const MODEL_190_I_SUBKEYS = [
    { key: '01', label: 'Cesión del derecho a la explotación del derecho de imagen' },
    { key: '02', label: 'Propiedad intelectual, tipo general del art. 101.9 LIRPF' },
    { key: '03', label: 'Resto de supuestos del art. 75.2 b) RIRPF (asistencia técnica, arrendamiento de negocios)' },
];
/** Subclave por defecto de un profesional que factura con el 15 % de siempre. */
export const MODEL_190_DEFAULT_KEY = 'G';
export const MODEL_190_DEFAULT_SUBKEY = '01';
/**
 * Subclaves válidas para una clave dada.
 *
 * ☠️ NUNCA DEVUELVE VACÍO PARA UNA CLAVE QUE SE OFREZCA. Si lo hiciera, el
 * desplegable de subclave se quedaría sin opciones y la ficha no podría cerrar
 * nunca el hueco de la subclave: incompleta para siempre, sin manera de
 * arreglarlo.
 */
export function subkeysForKey(key) {
    if (key === 'G')
        return MODEL_190_G_SUBKEYS;
    if (key === 'H')
        return MODEL_190_H_SUBKEYS;
    if (key === 'I')
        return MODEL_190_I_SUBKEYS;
    return [];
}
export const ProvinceCodeSchema = z
    .string()
    .regex(/^[0-9]{2}$/, 'El código de provincia son dos dígitos')
    .refine(isValidProvinceCode, 'Código de provincia inexistente en la tabla de la AEAT');
export const CountryCodeSchema = z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'El código de país son dos letras mayúsculas (ISO 3166-1 alfa-2)');
//# sourceMappingURL=aeat-parties.js.map